import { useFormik } from 'formik';
import { FC } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { cn } from 'rizzui';
import * as yup from 'yup';
import CustomSelectApiHookForm from '../../components/forms/CustomSelectApiHookForms';
import DateField from '../../components/forms/DateField';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import useContract from '../../hooks/api-calls/contract/useContracts';
import useUsers from '../../hooks/api-calls/useUsers';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import FormFooter from '../_layout/_footers/form-footer';

interface LeadFormProps {
    data?: any;
    submit: (values: any) => void;
    isLoading: boolean;
}

const initialValues = {
    leadId: '',
    userId: '',
    targetDate: '',
    contractId: '',
};

export const leadSchema = yup.object({
    leadId: yup.string().required('El ID del lead es obligatorio'),
    userId: yup.string().required('El usuario es obligatorio'),
    targetDate: yup.string().required('La fecha objetivo es obligatoria'),
    contractId: yup.string().required('El contrato es obligatorio'),
});

const LeadForm: FC<LeadFormProps> = ({ data, submit, isLoading }) => {

    // HOOKS

    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { getUsersList } = useUsers({ active: true });
    const { fetchContracts } = useContract();

    const formik = useFormik({
        initialValues: {
            ...initialValues,
            ...(data && {
                leadId: data.id || '',
                userId: data.assignedTo?.id || '',
                targetDate: data.expectedCloseDate ? data.expectedCloseDate.split(' ')[0] : '',
                contractId: data.originalContract?.id || '',
            }),
        },
        validationSchema: leadSchema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: values => { submit(values) },
    });

    // RENDER

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                            <FormGroup
                                title="Información del Lead"
                                description="Datos principales del lead"
                                titleCols="@md:col-span-3"
                                childCols={cn("@md:col-span-9 md:grid-cols-2")}
                            >
                                <CustomSelectApiHookForm
                                    id="contractId"
                                    label="Contrato"
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        return await fetchContracts({ search_text: searchText }) as any;
                                    }}
                                    error={formik.errors.contractId}
                                    minInputLength={2}
                                />

                                <div className="space-y-2">
                                    <CustomSelectApiHookForm
                                        id="userId"
                                        label="Usuario Asignado"
                                        formik={formik}
                                        fetchOptions={async () => {
                                            return getUsersList() as any;
                                        }}
                                        error={formik.errors.userId}
                                        isDisabled={!userCan('get_users_for_select', 'user')}
                                    />
                                    {!userCan('get_users_for_select', 'user') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los usuarios</span>
                                        </div>
                                    )}
                                </div>

                                <DateField
                                    id="targetDate"
                                    label="Fecha Objetivo"
                                    value={formik.values.targetDate}
                                    onChange={(date: any) => formik.setFieldValue('targetDate', date)}
                                    error={formik.errors.targetDate}
                                    className="w-full"
                                />
                            </FormGroup>
                        </div>
                    </form>
                </div>
            </>
        );
    };

    return (
        <>
            <Page container="fluid">
                {data && getContent()}
            </Page>
            <FormFooter
                submitBtnText={'Editar Renovación'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
}

export default LeadForm;